import React, { useContext, useEffect, useRef, useState } from 'react'
import { Card, Col, Flex, Form, Popover, Row, Select, Spin } from 'antd'
import { get, post } from '../../Common/api'
import { Color } from '../../Common/Color'
import dayjs from 'dayjs'
import TextArea from 'antd/es/input/TextArea'
import { CheckOutlined, WarningOutlined } from '@ant-design/icons'
import { t } from 'i18next'
import GlobalContext from '../GlobalContext'
import consumer from '../../Common/cable'
import { ActionButton } from '../../Common/Common'

const style = (justifyContent) => ({ color: Color.dark_grey, fontSize: '0.8em', justifyContent })

const Content = ({ message, align, multiPhone, contentStyle }) => (
  <div className="sms-content" style={contentStyle}>
    <div style={{ whiteSpace: 'pre-line' }}>{message.content}</div>
    <div style={{ lineHeight: '0.2', marginTop: '1em' }}>
      <div style={{ ...style(align), display: 'inline' }}>
        {dayjs(message.created_at).format('HH:mm:ss dddd DD')}
        {(message.status === 'DELIVERED') && <CheckOutlined style={{ color: Color.black, padding: '0.8em 1em' }} />}
        {(message.status === 'ENROUTE') && <Spin size="small" style={{ padding: '0 1em' }} />}
        {(message.status !== 'DELIVERED') && (message.status !== 'ENROUTE') &&
          <Popover content={t('Admin.SmsPanel.error.content', { status: message.status })} title={t('Admin.SmsPanel.error.title')}>
            <WarningOutlined style={{ color: Color.red, padding: '0.8em 1em' }} />
          </Popover>}
      </div>
      {multiPhone && message.phone &&
        <div style={{ ...style(align) }}>{t('Admin.SmsPanel.send_to')}{message.phone}</div>
      }
      <div style={{ ...style(align), display: 'grid' }}>{message.sender}</div>
    </div>
  </div>
)

const DistantMessage = ({ message }) => (
  <Flex align='flex-end' justify='flex-start'>
    <Row >
      <Col style={{ display: 'flex', justifyContent: 'flex-start', flexDirection: 'column' }} span={2} >
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" >
          <polygon points="40 0, 100 60, 100 0" fill={Color.light_blue} />
        </svg>
        <span style={{ fontSize: '0.7em', paddingRight: '0.3em' }}>{message.initial}</span>
      </Col>
      <Col style={{ display: 'block', justifyContent: 'start', backgroundColor: Color.light_blue, padding: '1em', borderRadius: '0 1em 1em 1em' }} span={22} >
        <Content message={message} align='start' />
      </Col>
    </Row>
  </Flex>
)

const LbmMessage = ({ message, multiPhone }) => (
  <Flex align='flex-end' justify='flex-end' style={{ margin: '1em' }}>
    <Row >
      <Col style={{ display: 'block', justifyContent: 'end', backgroundColor: Color.light_green, padding: '1em', borderRadius: '1em 1em 0 1em' }} span={22}>
        <Content message={message} multiPhone={multiPhone} align='end' />
      </Col>
      <Col style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column' }} span={2} >
        <span style={{ fontSize: '0.7em', paddingLeft: '0.3em' }}>{t('Admin.SmsPanel.lbm_initial')}</span>
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" >
          <polygon points="0 40, 60 100, 0 100" fill={Color.light_green} />
        </svg>
      </Col>
    </Row>
  </Flex>
)

export default function SMSPanel ({ owner, ownerType, style = {} }) {
  const [form] = Form.useForm()
  const [messages, setMessages] = useState([])
  const [templates, setTemplates] = useState([])
  const [selectedPhone, setSelectedPhone] = useState(null)
  const [disableSend, setDisableSend] = useState(false)
  const [emptyText, setEmptyText] = useState(true)
  const [newMessage, setNewMessage] = useState()
  const [chat, setChat] = useState()

  const multiPhone = owner.phones?.length > 1
  const { notificationApi } = useContext(GlobalContext)

  useEffect(() => {
    if (!owner?.id) { return }
    getMessages()
    getTemplates()
    if (owner.phones?.length === 1) { setSelectedPhone(owner.phones[0].value) }
    if (owner.phones?.length > 1) { setDisableSend(true) }
    setChat(owner.chat)
  }, [owner])

  const unsubscribeRef = useRef(null)

  useEffect(() => {
    if (newMessage) { receivedMessage(newMessage) }
  }, [newMessage])

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) { unsubscribeRef.current() }
    }
  }, [])

  const receivedMessage = (message) => {
    const index = messages.findIndex((msg) => (msg.id === message.id))
    if (index >= 0) {
      setMessages([
        ...messages.slice(0, index),
        message,
        ...messages.slice(index + 1, messages.length)
      ])
    } else {
      setMessages([...messages, message])
    }
  }

  const subscribe = (chatId) => {
    if (!chatId) { return }
    if (unsubscribeRef.current) { unsubscribeRef.current() }
    const subscription = consumer.subscriptions.create({ channel: 'MessagesChannel', chat_id: chatId }, {
      connected: () => { console.log('message connected : ', chatId) },
      disconnected: () => { console.log('message disconnected', chatId) },
      received: (data) => { setNewMessage(data.message); setChat(data.chat) }
    })
    unsubscribeRef.current = () => { subscription.unsubscribe() }
  }

  const getMessages = async () => {
    await get({
      url: `admin/${ownerType}/${owner.id}/messages`,
      params: {},
      onSuccess: (json) => {
        setMessages(json.messages)
        subscribe(json.messages[0] && json.messages[0].chat_id)
      }
    })
  }

  const getTemplates = async () => {
    await get({
      url: 'admin/sms_templates',
      params: {},
      onSuccess: (json) => {
        setTemplates(json.sms_templates.map((template) => ({ label: template.name, value: template.content })))
      }
    })
  }

  const sendMessage = async (values) => {
    setEmptyText(true)
    const phone = selectedPhone || values.selected_phone
    await post({
      url: `admin/${ownerType}/${owner.id}/messages`,
      params: { content: values.message, selected_phone: phone },
      notification: { api: notificationApi, state: 'create' },
      onSuccess: (res) => {
        form.setFieldValue('message', '')
        setMessages([...messages, res].sort((a, b) => dayjs(a.created_at).diff(dayjs(b.created_at))))
      }
    })
  }

  const isAdmin = (message) => (!!message.user_id)

  const closeChat = async () => {
    await post({
      url: `admin/chats/${chat.id}/close`,
      params: {},
      notification: { api: notificationApi, state: 'update' },
      onSuccess: () => {
        setChat({ ...chat, open: false })
      }
    })
  }

  if (!owner || !chat) { return null }

  return (
    <div>
      <Card title='SMS' style={{ maxHeight: '31.5em', overflowY: 'scroll', ...style }}>
        <Flex vertical gap='small'>
          {messages.map((message) => (
            isAdmin(message)
              ? <LbmMessage key={message.id} message={message} multiPhone={multiPhone} />
              : <DistantMessage key={message.id} message={message} />
          ))}
        </Flex>
      </Card>
      < Form style={{ paddingTop: '1em' }} layout="vertical" name="chatForm" onFinish={sendMessage} form={form} >
        <Form.Item name="message">
          <TextArea rows={5} placeholder="Votre SMS"
            onChange={(e) => {
              setEmptyText(e.target.value.length < 3)
              form.setFieldValue('template', null)
            }} />
        </Form.Item>

        <Form.Item name="template">
          <Select
            options={templates}
            placeholder={t('Admin.SmsPanel.select_template')}
            onChange={(value) => {
              setEmptyText(false)
              form.setFieldValue('message', value)
            }}
          />
        </Form.Item>
        {owner.phones?.length > 1 &&
          <Form.Item name="selected_phone">
            <Select
              options={owner.phones.map((o) => ({ label: `${o.description} : ${o.value}`, value: o.value }))}
              placeholder={t('Admin.SmsPanel.select_phone')}
              onChange={() => (setDisableSend(false))}
            />
          </Form.Item>
        }
        <Flex justify='space-around'>
          <ActionButton type="primary" htmlType="submit" disabled={disableSend || emptyText}>
            Envoyer
          </ActionButton>
          <ActionButton onClick={closeChat} disabled={!chat.open}>{t('Admin.Chat.close')}</ActionButton>
        </Flex>
      </Form >
    </div>
  )
}
